import { cn } from "@/lib/utils";
import { setDraggingComponent } from "@/store/iframeSlice";
import { useCommunityPanelStore } from "@/store/zustand/communityPanelStore";
import type { CollectionType, LayoutOption } from "@/utils/types";
import {
  Blocks,
  Grid2X2,
  LayoutPanelLeft,
  LayoutPanelTop,
  LibraryIcon,
  type LucideIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

interface BaseCardProps {
  title?: string;
  titleTooltip?: string;
  subtitle?: React.ReactNode;
  count?: number;
  imageUrl?: string;
  selected?: boolean;
  onClick?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  className?: string;
  isThumbnail?: boolean;
  lightBackground?: boolean;
  isLibraryComponent?: boolean;
}
export const BaseCard = React.memo(
  ({
    title,
    titleTooltip,
    subtitle,
    count,
    imageUrl,
    selected,
    onClick,
    onDragStart,
    className,
    isThumbnail,
    onDragEnd,
    lightBackground,
    isLibraryComponent = false,
  }: BaseCardProps) => {
    const [PlaceholderIcon, setPlaceholderIcon] = useState<LucideIcon | null>(
      null,
    );

    // Track which images have failed to load
    const [imageError, setImageError] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
      const placeholderIcons: LucideIcon[] = [
        Blocks,
        Grid2X2,
        LayoutPanelTop,
        LayoutPanelLeft,
      ];
      setPlaceholderIcon(
        () =>
          placeholderIcons[Math.floor(Math.random() * placeholderIcons.length)],
      );
    }, []);

    // Reset image error when imageUrl changes
    useEffect(() => {
      setImageError(false);
    }, [imageUrl]);

    const handleImageError = () => {
      setImageError(true);
    };

    return (
      <div
        className={cn(
          "p-2 rounded-lg cursor-pointer transition-all border w-full relative",
          isThumbnail
            ? "bg-secondary-n-950 hover:bg-secondary-n-700"
            : "bg-background-primary hover:border-primary-0",
          className,
          selected ? "border-primary-0" : "border-border-primary",
        )}
        onClick={onClick}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragEnd={(e) => {
          onDragEnd?.(e);
          dispatch(setDraggingComponent(null));
        }}
      >
        {isLibraryComponent && (
          <div className="absolute top-3 left-0 z-10 h-6 pl-2 pr-2 rounded-r-full bg-primary-n-500 flex items-center justify-center shadow-md">
            <LibraryIcon className="w-3.5 h-3.5 text-white" />
          </div>
        )}
        <div
          className="w-full min-h-[40px] h-full rounded-lg flex items-center justify-center"
          style={{
            background: "linear-gradient(90deg, #1F1F22 0%, #2F3035 100%)",
          }}
        >
          {isThumbnail ? (
            <div
              className={cn(
                "h-full w-full p-2 flex items-center justify-center overflow-hidden rounded-sm",
                lightBackground ? "bg-secondary-n-50" : "bg-background-primary",
              )}
            >
              {imageUrl && !imageError ? (
                <img
                  src={imageUrl}
                  alt={title}
                  className="object-scale-down rounded-sm max-w-full max-h-full"
                  draggable={false}
                  onError={handleImageError}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center rounded-sm">
                  {PlaceholderIcon && (
                    <PlaceholderIcon className="w-8 h-8 text-gray-400" />
                  )}
                </div>
              )}
            </div>
          ) : imageUrl && !imageError ? (
            <div className="rounded-sm overflow-hidden w-full h-full">
              <img
                src={imageUrl}
                alt={title}
                className="w-min-content object-scale-down"
                draggable={false}
                onError={handleImageError}
              />
            </div>
          ) : (
            <div className="w-full min-h-[73px] flex items-center justify-center rounded-sm">
              {PlaceholderIcon && (
                <PlaceholderIcon className="w-10 h-10 text-gray-400" />
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between mt-2 items-center select-none">
          <span className="text-2xs font-medium text-secondary-n-50 truncate">
            {title}
          </span>
          {count !== undefined && (
            <span className="text-xs text-primary rounded-xs bg-secondary-300 px-2 py-1">
              {count}
            </span>
          )}
        </div>
        {subtitle && (
          <div className="flex items-center">
            <span className="text-3xs font-medium text-secondary-n-500 truncate">
              {subtitle}
            </span>
          </div>
        )}
      </div>
    );
  },
);

export const LayoutCard: React.FC<{
  layout: LayoutOption;
  selected?: boolean;
  onClick: () => void;
  count?: number;
}> = ({ layout, selected, onClick, count = 0 }) => {
  return (
    <BaseCard
      title={layout.name}
      count={count}
      imageUrl={layout.icon}
      selected={selected}
      onClick={onClick}
    />
  );
};
export const CollectionCard: React.FC<{
  collection: CollectionType;
  count: number;
  onSelect: () => void;
}> = ({ collection, count, onSelect }) => {
  const { selectedCollections } = useCommunityPanelStore();
  const isSelected = selectedCollections.includes(collection.id.toString());

  return (
    <BaseCard
      title={collection.name}
      count={count}
      imageUrl={collection.image_url || undefined}
      selected={isSelected}
      onClick={onSelect}
    />
  );
};
